import {
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import ModalContainerModule from "../modules/modalContainer";
import { useEffect, useMemo, useState } from "react";
import { getCountries } from "../../services/list.service";
import {
  formatShortDateTime,
  isEmptyArray,
  isEmptyString,
  isNullUndefined,
  maskPhoneNumber,
} from "../../util/util";
import {
  fetchOrganization,
  getDuplicatedOrganizationsByName,
  getRelationshipsToOrganization,
  mergeOrganizations,
  updateCollapseOrganization,
} from "../../services/organisation.service";
import { ResponsiveRow } from "../../constants/layout.constants";
import ErrorIcon from "@mui/icons-material/DoDisturbAlt";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import MuiDataGrid from "../modules/MuiDataGrid";
import { Box } from "@mui/system";
import { issueResponseMessage } from "../../actions/message";
import {
  ColumnContainer,
  DetailsColumn,
  DetailsLabel,
  DetailsRow,
  DetailsText,
  ModalAcceptButton,
  ModalCancelButton,
} from "../../constants/component.constants";
import { personOrgSearch } from "../../services/shared.service";

// TODO: OPEN A MODAL TO VIEW AND POSSIBLY EDIT THE ORGANIZATION DETAILS
// Fetch the duplicate organizations that match the selected organization
// List the possible matches and allow for editing the selected organization
// Allow for selection of organizations to collapse into the selected organization
// On submit, update the selected organization's details and collapse the selected organization into the selected organization

export default function CollapseOrganizationModal({
  open,
  onClose,
  organization,
  task,
}) {
  const [selectedOrg, setSelectedOrg] = useState(organization);
  const [countryOptions, setCountryOptions] = useState([]);
  const [possibleOrganizationsForMerge, setPossibleOrganizationsForMerge] =
    useState([]);
  const [
    selectedOrganizationsForCollapse,
    setSelectedOrganizationsForCollapse,
  ] = useState([]);
  const [selectedOrganizationForMerge, setSelectedOrganizationForMerge] =
    useState(null);
  const [relationships, setRelationships] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState(null);
  const [formStage, setFormStage] = useState(1);
  const [foundOrganizations, setFoundOrganizations] = useState(null);
  const [noFoundUser, setNoFoundUser] = useState(false);

  function handleCountryChange(event) {
    const value = event.target.value;
    setSelectedOrg((prevOrg) => ({
      ...prevOrg, // Copy properties of the existing selectedOrg
      address: {
        ...prevOrg.address, // Copy properties of the existing address object
        countryCd: value, // Update the countryCd property
      },
    }));
  }

  const handleAccept = () => {
    // Update the selected organization's details and collapse the selected organization into the selected organization
    // TODO: Implement the logic to update the selected organization's details and collapse the selected organization into the selected organization
    // TODO: consider the task
    if (task === "edit") {
      const data = {
        ...selectedOrg,
        collapsedOrganizations: selectedOrganizationsForCollapse,
      };
      updateCollapseOrganization(data).then((res) => {
        issueResponseMessage(res);
        onClose();
      });
    } else if (task === "merge") {
      mergeOrganizations(
        selectedOrg.id.split("_")[0],
        selectedOrganizationForMerge.id,
      ).then((res) => {
        issueResponseMessage(res);
        onClose();
      });
    }
  };

  const columns = useMemo(() => [
    {
      headerName: "Name",
      field: "name",
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "Description",
      field: "description",
      minWidth: 150,
      flex: 1.5,
    },
    { headerName: "Status", field: "status", minWidth: 200, flex: 2 },
    {
      headerName: "System Organization",
      field: "systemOrganization",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Chip
            icon={
              params.row.systemOrganization ? (
                <VerifiedUserIcon />
              ) : (
                <ErrorIcon />
              )
            }
            label={params.row.systemOrganization ? "Yes" : "No"}
            color={params.row.systemOrganization ? "success" : "error"}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        );
      },
    },
    {
      headerName: "Has Address",
      field: "addressId",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Chip
            icon={params.row.addressId ? <VerifiedUserIcon /> : <ErrorIcon />}
            label={params.row.addressId ? "Yes" : "No"}
            color={params.row.addressId ? "success" : "error"}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        );
      },
    },
    {
      headerName: "Has Contacts",
      field: "contact",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Chip
            icon={params.row.contact.id ? <VerifiedUserIcon /> : <ErrorIcon />}
            label={params.row.contact.id ? "Yes" : "No"}
            color={params.row.contact.id ? "success" : "error"}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        );
      },
    },
  ]);

  const fetchDuplicateOrganization = async (orgName) => {
    try {
      const data = await getDuplicatedOrganizationsByName(orgName);
      setSelectedOrg(data.organization);
      setPossibleOrganizationsForMerge(data.duplicates);
    } catch (error) {
      console.error("Error fetching duplicate organization:", error);
      return null;
    }
  };

  const fetchRelationships = async (orgId) => {
    try {
      const data = await getRelationshipsToOrganization(orgId);
      setRelationships(data);
    } catch (error) {
      console.error("Error fetching relationships:", error);
      return null;
    }
  };

  function searchOrg() {
    setFormStage(2);
    let data = {
      searchType: "Company",
      organizationName: companySearch,
    };
    personOrgSearch(data)
      .then((response) => {
        // filter out the organization selected to be merged
        const filtered = response.filter(
          (o) => o.id.split("_")[0] !== selectedOrg.id,
        );
        setFoundOrganizations(filtered);
        setNoFoundUser(false);
        if (isEmptyArray(response)) {
          setNoFoundUser(true);
          setFoundOrganizations([]);
        }
        setCompanySearch("");
      })
      .catch((error) => {
        console.error("PersonOrg Error: ", error);
      });
  }

  function selectOrg(org) {
    setSelectedOrg(org);
    fetchOrganization(org.id.split("_")[0])
      .then((response) => {
        setSelectedOrganizationForMerge(selectedOrg);
        setSelectedOrg(response);
        setFormStage(3);
      })
      .catch((error) => {
        console.error("Fetch Organization Error: ", error);
      });
  }

  useEffect(() => {
    const initialize = async () => {
      getCountries().then((countryOptions) =>
        setCountryOptions(countryOptions),
      );

      if (!isNullUndefined(organization?.id)) {
        setLoading(true);
        const orgId = organization.id;

        try {
          await fetchDuplicateOrganization(orgId);

          if (task === "view") {
            await fetchRelationships(orgId);
          }
        } catch (error) {
          console.error("Failed to load organization data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    initialize();
  }, []);

  return (
    <ModalContainerModule
      size="md"
      cancel={onClose}
      openModal={open}
      accept={task === "view" ? null : handleAccept}
      loading={loading}
      acceptButtonText={
        task === "view"
          ? ""
          : task === "edit"
            ? "Edit"
            : task === "merge"
              ? "Merge"
              : ""
      }
      modalTitle={
        task === "view"
          ? `View ${selectedOrg?.name}`
          : task === "edit"
            ? `Edit ${selectedOrg?.name}`
            : task === "merge"
              ? `Collapse ${selectedOrganizationForMerge?.name && formStage === 3 ? selectedOrganizationForMerge?.name + " into " : ""} ${selectedOrg?.name}`
              : "Unknown Task"
      }
      acceptDisabled={task === "merge" && formStage !== 3}
    >
      {loading && <CircularProgress />}
      {!loading &&
        (task === "edit" || (task === "merge" && formStage === 3)) && (
          <>
            <ResponsiveRow sx={{ height: "auto" }} container>
              <FormControl
                item
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel required>Company Name</InputLabel>
                <OutlinedInput
                  label={"Company Name"}
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"companyName"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedOrg((prev) => ({ ...prev, name: value }));
                  }}
                  value={selectedOrg?.name}
                />
              </FormControl>

              <FormControl
                item
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel required>Address 1</InputLabel>
                <OutlinedInput
                  label={"Address 1"}
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"address1"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedOrg((prev) => ({
                      ...prev,
                      address: { ...prev.address, address1: value },
                    }));
                  }}
                  value={selectedOrg?.address?.address1}
                />
              </FormControl>

              <FormControl
                item
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel>Address 2</InputLabel>
                <OutlinedInput
                  label={"Address 2"}
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"address2"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedOrg((prev) => ({
                      ...prev,
                      address: { ...prev.address, address2: value },
                    }));
                  }}
                  value={selectedOrg?.address?.address2}
                />
              </FormControl>

              <FormControl
                item
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel required>Country</InputLabel>
                <Select
                  label="Country"
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"country"}
                  value={selectedOrg?.address?.countryCd}
                  onChange={(event) => handleCountryChange(event)}
                >
                  {countryOptions && countryOptions?.length > 0 ? (
                    countryOptions.map(function (country, i) {
                      return (
                        <MenuItem key={country.value} value={country.value}>
                          {country.label}
                        </MenuItem>
                      );
                    }, this)
                  ) : (
                    <MenuItem sx={{ width: "100%" }}>No Results Found</MenuItem>
                  )}
                </Select>
              </FormControl>

              <FormControl
                item
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel required>City</InputLabel>
                <OutlinedInput
                  label={"City"}
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"city"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedOrg((prev) => ({
                      ...prev,
                      address: { ...prev.address, city: value },
                    }));
                  }}
                  value={selectedOrg?.address?.city}
                />
              </FormControl>

              <FormControl
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel>Description</InputLabel>
                <OutlinedInput
                  label={"Description"}
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"description"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedOrg((prev) => ({ ...prev, description: value }));
                  }}
                  value={selectedOrg?.description}
                />
              </FormControl>

              <FormControl
                sx={{
                  width: { xs: "100%", lg: "33.3%" },
                  marginBottom: "10px",
                }}
                disabled={selectedOrg?.systemOrganization}
              >
                <InputLabel>Tax ID</InputLabel>
                <OutlinedInput
                  label={"Tax Id"}
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"taxId"}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedOrg((prev) => ({ ...prev, taxId: value }));
                  }}
                  value={selectedOrg?.taxId}
                />
              </FormControl>
            </ResponsiveRow>
            <Divider sx={{ width: "100%", marginBottom: "20px" }} />
            {task !== "merge" && (
              <Alert severity="info">
                <AlertTitle>Collapsing Organizations</AlertTitle>
                By selecting the organizations below and hitting the "EDIT"
                button, you can modify the details of the selected organization
                and merge the selected organization into the organization you
                are editing.
                <br />
                Adjusting the organization details without selecting any
                organizations below will not collapse the organizations in the
                list.
              </Alert>
            )}
          </>
        )}
      {!loading && task === "view" && (
        <ResponsiveRow width={"100%"} sx={{ height: "fit-content" }}>
          {relationships?.assets?.length <= 0 &&
          relationships?.subscriptions?.length <= 0 &&
          relationships?.leases?.length <= 0 &&
          relationships?.ownerships?.length <= 0 &&
          relationships?.facilities?.length <= 0 &&
          relationships?.vendorFor?.length <= 0 ? (
            <Typography variant="body1" color="text.secondary">
              No Relationships found.
            </Typography>
          ) : (
            <Box sx={{ width: "100%", height: "auto" }}>
              <Typography variant="body1" color="text.secondary">
                Relationships Found
              </Typography>
              <ResponsiveRow>
                {relationships?.assets?.length > 0 && (
                  <Paper
                    sx={{
                      padding: 2,
                      width: "48%",
                      height: 150,
                      overflowY: "auto",
                    }}
                  >
                    <Typography variant="body3" color="text.secondary">
                      Assets
                    </Typography>
                    {relationships?.assets.map((asset) => (
                      <Box
                        key={`${asset.id}_A`}
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginBottom: 1,
                          padding: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Name:</span>{" "}
                          {asset.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Status:</span>{" "}
                          {asset?.status?.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Start:</span>{" "}
                          {formatShortDateTime(asset.dateAcquired)}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
                {relationships?.subscriptions?.length > 0 && (
                  <Paper
                    sx={{
                      padding: 2,
                      width: "48%",
                      height: 150,
                      overflowY: "auto",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Subscriptions
                    </Typography>
                    {relationships?.subscriptions.map((subscription) => (
                      <Box
                        key={`${subscription.id}_S`}
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginBottom: 1,
                          padding: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Name:</span>{" "}
                          {subscription.subscriberName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Status:</span>{" "}
                          {subscription.status.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Billing Code:</span>{" "}
                          {subscription.billingCode}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
                {relationships?.leases?.length > 0 && (
                  <Paper
                    sx={{
                      padding: 2,
                      width: "48%",
                      height: 150,
                      overflowY: "auto",
                    }}
                    key="leases"
                  >
                    <Typography variant="h6" color="text.secondary">
                      Leases({relationships?.leases?.length})
                    </Typography>
                    {relationships?.leases.map((lease) => (
                      <Box
                        key={`${lease.id}_L`}
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginBottom: 1,
                          padding: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Code:</span>{" "}
                          {lease.billingCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Status:</span>{" "}
                          {lease.status.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Facility:</span>{" "}
                          {lease.facilityName}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
                {relationships?.ownerships?.length > 0 && (
                  <Paper
                    sx={{
                      padding: 2,
                      width: "48%",
                      height: 150,
                      overflowY: "auto",
                    }}
                    key="ownerships"
                  >
                    <Typography variant="body1" color="text.secondary">
                      Ownerships
                    </Typography>
                    {relationships?.ownerships.map((ownership) => (
                      <Box
                        key={`${ownership.id}_O`}
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginBottom: 1,
                          padding: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Code:</span>{" "}
                          {ownership.billingCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Status:</span>{" "}
                          {ownership?.status?.name ?? ownership?.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Facility:</span>{" "}
                          {ownership?.facilityName}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
                {relationships?.facilities?.length > 0 && (
                  <Paper
                    sx={{
                      padding: 2,
                      width: "48%",
                      height: 150,
                      overflowY: "auto",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Facilities
                    </Typography>
                    {relationships?.facilities.map((facility) => (
                      <Box
                        key={`${facility.id}_F`}
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginBottom: 1,
                          padding: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <span style={{ fontWeight: "bold" }}>Name:</span>{" "}
                          {facility.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span style={{ fontWeight: "bold" }}>Status:</span>{" "}
                          {facility.statusCd}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span style={{ fontWeight: "bold" }}>Start:</span>{" "}
                          {formatShortDateTime(facility.dateCreated)}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
                {relationships?.vendorFor?.length > 0 && (
                  <Paper
                    sx={{
                      padding: 2,
                      width: "48%",
                      height: 150,
                      overflowY: "auto",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Vendor For
                    </Typography>
                    {relationships?.vendorFor.map((subscriber) => (
                      <Box
                        key={`${subscriber.id}_VO`}
                        sx={{
                          display: "flex",
                          gap: 2,
                          marginBottom: 1,
                          padding: 1,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Name:</span>{" "}
                          {subscriber.subscriberName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Status:</span>{" "}
                          {subscriber.status.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <span sx={{ fontWeight: "bold" }}>Billing Code:</span>{" "}
                          {subscriber.billingCode}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </ResponsiveRow>
            </Box>
          )}
        </ResponsiveRow>
      )}
      {!loading && task === "merge" && (
        <>
          {formStage === 1 ? (
            <ResponsiveRow>
              <Alert
                sx={{
                  width: "100%",
                  height: "fit-content",
                  padding: "10px 20px",
                }}
                severity="info"
              >
                <Typography variant="body2" sx={{ width: "100%" }}>
                  Start by searching the <strong>Company</strong> by name
                </Typography>
              </Alert>
              <FormControl sx={{ width: { xs: "100%", lg: "75%" } }}>
                <InputLabel>Company Name</InputLabel>
                <OutlinedInput
                  label="Company Name"
                  sx={{ width: { xs: "100%", md: "90%" } }}
                  id={"companySearch"}
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                />
              </FormControl>
              <ModalAcceptButton
                onClick={searchOrg}
                disabled={isEmptyString(companySearch)}
                sx={{
                  width: { xs: "100%", lg: "200px" },
                  height: "50px",
                }}
              >
                Search
              </ModalAcceptButton>
            </ResponsiveRow>
          ) : formStage === 2 ? (
            <ResponsiveRow
              sx={{
                width: "100%",
                height: "auto",

                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h5"
                color="primary.dark"
                textAlign="start"
                sx={{ width: "100%", marginBottom: "10px" }}
              >
                Search Results ({foundOrganizations?.length})
              </Typography>
              <ResponsiveRow
                sx={{
                  flexDirection: { xs: "column", lg: "row" },
                  flexWrap: { xs: "nowrap", lg: "wrap" },

                  width: "100%",
                  height: "auto",

                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {foundOrganizations &&
                  foundOrganizations?.length > 0 &&
                  foundOrganizations.map(function (customer, i) {
                    return (
                      <ColumnContainer
                        key={customer.id}
                        sx={{
                          width: "100%",
                          height: "auto",

                          borderRadius: "10px",
                          backgroundColor: "#efefef",

                          "&:hover": {
                            backgroundColor: "#dfdfdf",
                          },
                          margin: "5px",
                          padding: "10px",
                        }}
                      >
                        <Typography
                          fontWeight={800}
                          variant="h6"
                          color="primary.dark"
                          sx={{
                            textAlign: "start",
                          }}
                        >
                          Confirm Organization Details
                        </Typography>
                        <ResponsiveRow
                          sx={{ flexDirection: { xs: "column", md: "row" } }}
                        >
                          <DetailsColumn
                            sx={{
                              width: { xs: "100%", md: "50%" },
                              paddingBottom: { xs: "0px", md: "10px" },
                            }}
                          >
                            <DetailsRow>
                              <DetailsLabel>Name:</DetailsLabel>
                              <DetailsText>
                                {customer.name ?? "N/A"}
                              </DetailsText>
                            </DetailsRow>
                            <DetailsRow>
                              <DetailsLabel>Email:</DetailsLabel>
                              <DetailsText sx={{ wordBreak: "break-word" }}>
                                {customer.email ?? "N/A"}
                              </DetailsText>
                            </DetailsRow>
                            <DetailsRow>
                              <DetailsLabel>Phone:</DetailsLabel>
                              <DetailsText>
                                {maskPhoneNumber(customer.mobilePhone) ?? "N/A"}
                              </DetailsText>
                            </DetailsRow>
                          </DetailsColumn>
                          <DetailsColumn
                            sx={{
                              width: { xs: "100%", md: "50%" },
                              paddingTop: { xs: "0px", md: "10px" },
                            }}
                          >
                            <DetailsRow>
                              <DetailsLabel>Description:</DetailsLabel>
                              <DetailsText>
                                {customer.description ?? "N/A"}
                              </DetailsText>
                            </DetailsRow>
                          </DetailsColumn>
                        </ResponsiveRow>
                        <ModalAcceptButton
                          onClick={() => selectOrg(customer)}
                          sx={{ width: "100px" }}
                        >
                          Confirm
                        </ModalAcceptButton>
                      </ColumnContainer>
                    );
                  })}
              </ResponsiveRow>
              {noFoundUser && (
                <ResponsiveRow
                  sx={{
                    flexDirection: "column",
                    flexWrap: "nowrap",

                    width: "100%",
                    height: "auto",

                    justifyContent: "flex-start",
                    alignItems: "flex-start",

                    margin: 0,
                    padding: 0,
                  }}
                >
                  <Alert
                    sx={{
                      width: "100%",
                      height: "auto",

                      overflow: "hidden",
                      margin: 0,
                    }}
                    severity="error"
                  >
                    <AlertTitle color="error">No Companies found</AlertTitle>
                    <Typography sx={{ width: "100%" }}>
                      There were no companies found with that Company Name.
                    </Typography>
                    <Typography sx={{ width: "100%" }}>
                      Try searching a different Company?
                    </Typography>
                    <ResponsiveRow
                      sx={{
                        width: "100%",
                        justifyContent: "flex-end",
                        height: "auto",
                      }}
                    >
                      <ModalCancelButton
                        onClick={() => setFormStage(1)}
                        sx={{ width: { xs: "100%", lg: "100px" } }}
                      >
                        Cancel
                      </ModalCancelButton>
                    </ResponsiveRow>
                  </Alert>
                </ResponsiveRow>
              )}
            </ResponsiveRow>
          ) : formStage === 3 ? (
            <>
              <ResponsiveRow
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  height: "auto",
                }}
              >
                <Typography variant="h3">
                  Organization to be deleted/collapsed
                </Typography>
                <ColumnContainer
                  key={selectedOrganizationForMerge.id}
                  sx={{
                    width: "100%",
                    height: "auto",

                    borderRadius: "10px",
                    backgroundColor: "#efefef",

                    "&:hover": {
                      backgroundColor: "#dfdfdf",
                    },
                    margin: "5px",
                    padding: "10px",
                  }}
                >
                  <Typography
                    fontWeight={800}
                    variant="h6"
                    color="primary.dark"
                    sx={{
                      textAlign: "start",
                    }}
                  >
                    Confirm Organization Details
                  </Typography>
                  <ResponsiveRow
                    sx={{ flexDirection: { xs: "column", md: "row" } }}
                  >
                    <DetailsColumn
                      sx={{
                        width: { xs: "100%", md: "50%" },
                        paddingBottom: { xs: "0px", md: "10px" },
                      }}
                    >
                      <DetailsRow>
                        <DetailsLabel>Name:</DetailsLabel>
                        <DetailsText>
                          {selectedOrganizationForMerge.name ?? "N/A"}
                        </DetailsText>
                      </DetailsRow>
                      <DetailsRow>
                        <DetailsLabel>Email:</DetailsLabel>
                        <DetailsText sx={{ wordBreak: "break-word" }}>
                          {selectedOrganizationForMerge.email ?? "N/A"}
                        </DetailsText>
                      </DetailsRow>
                      <DetailsRow>
                        <DetailsLabel>Phone:</DetailsLabel>
                        <DetailsText>
                          {maskPhoneNumber(
                            selectedOrganizationForMerge.mobilePhone,
                          ) ?? "N/A"}
                        </DetailsText>
                      </DetailsRow>
                    </DetailsColumn>
                    <DetailsColumn
                      sx={{
                        width: { xs: "100%", md: "50%" },
                        paddingTop: { xs: "0px", md: "10px" },
                      }}
                    >
                      <DetailsRow>
                        <DetailsLabel>Description:</DetailsLabel>
                        <DetailsText>
                          {selectedOrganizationForMerge.description ?? "N/A"}
                        </DetailsText>
                      </DetailsRow>
                    </DetailsColumn>
                  </ResponsiveRow>
                </ColumnContainer>
                <ModalCancelButton
                  onClick={() => {
                    setFormStage(2);
                    setSelectedOrg(selectedOrganizationForMerge);
                  }}
                  sx={{ width: { xs: "100%", lg: "100px" } }}
                >
                  Back
                </ModalCancelButton>
              </ResponsiveRow>
            </>
          ) : null}
        </>
      )}
      {task !== "merge" && (
        <MuiDataGrid
          dataGridColumns={columns}
          dataGridRows={possibleOrganizationsForMerge}
          loading={loading}
          height="35vh"
          totalRows={possibleOrganizationsForMerge?.length || 0}
          serverPagination={false}
          noSelection={task === "view"}
          handleSelectedRows={setSelectedOrganizationsForCollapse}
          currentSelectionModel={selectedOrganizationsForCollapse}
        />
      )}
    </ModalContainerModule>
  );
}
